import React, { useState } from 'react';
import { supabase } from '../supabaseClient';
import { Plus } from 'lucide-react';

const AssetLiabilityManager = ({ session, assets, liabilities, onAssetsChange, onLiabilitiesChange }) => {
  const [showAssetForm, setShowAssetForm] = useState(false);
  const [showLiabilityForm, setShowLiabilityForm] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [editingLiability, setEditingLiability] = useState(null);
  const [assetForm, setAssetForm] = useState({
    name: '',
    value: '',
    category: 'cash',
    group: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [liabilityForm, setLiabilityForm] = useState({
    name: '',
    value: '',
    monthly_payment: '',
    interest_rate: '',
    rate_type: 'fixed',
    category: 'credit_card',
    group: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [loading, setLoading] = useState(false);

  const assetCategories = [
    { value: 'cash', label: 'Cash & Savings' },
    { value: 'investment', label: 'Investments' },
    { value: 'real_estate', label: 'Real Estate' },
    { value: 'vehicle', label: 'Vehicle' },
    { value: 'other', label: 'Other Assets' }
  ];

  const liabilityCategories = [
    { value: 'credit_card', label: 'Credit Card' },
    { value: 'mortgage', label: 'Mortgage' },
    { value: 'student_loan', label: 'Student Loan' },
    { value: 'auto_loan', label: 'Auto Loan' },
    { value: 'personal_loan', label: 'Personal Loan' },
    { value: 'other', label: 'Other Debt' }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getCategoryIcon = (category, type) => {
    const icons = {
      cash: '💰',
      investment: '📈',
      real_estate: '🏠',
      vehicle: '🚗',
      credit_card: '💳',
      mortgage: '🏡',
      student_loan: '🎓',
      auto_loan: '🚙',
      personal_loan: '💸',
      other: '📄'
    };
    return icons[category] || '📄';
  };

  const addAsset = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingAsset) {
        // Update existing asset
        const { error } = await supabase
          .from('assets')
          .update({
            name: assetForm.name,
            value: parseFloat(assetForm.value),
            category: assetForm.category,
            group: assetForm.group || null,
            date: assetForm.date
          })
          .eq('id', editingAsset.id);

        if (error) throw error;
        setEditingAsset(null);
      } else {
        // Add new asset
        const { error } = await supabase
          .from('assets')
          .insert({
            user_id: session.user.id,
            name: assetForm.name,
            value: parseFloat(assetForm.value),
            category: assetForm.category,
            group: assetForm.group || null,
            date: assetForm.date
          });

        if (error) throw error;
      }

      setAssetForm({
        name: '',
        value: '',
        category: 'cash',
        group: '',
        date: new Date().toISOString().split('T')[0]
      });
      setShowAssetForm(false);
      onAssetsChange();
    } catch (error) {
      console.error('Error with asset:', error);
      alert('Error with asset: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const addLiability = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingLiability) {
        // Update existing liability
        const { error } = await supabase
          .from('liabilities')
          .update({
            name: liabilityForm.name,
            value: parseFloat(liabilityForm.value),
            monthly_payment: liabilityForm.monthly_payment ? parseFloat(liabilityForm.monthly_payment) : null,
            interest_rate: liabilityForm.interest_rate ? parseFloat(liabilityForm.interest_rate) : null,
            rate_type: liabilityForm.rate_type,
            category: liabilityForm.category,
            group: liabilityForm.group || null,
            date: liabilityForm.date
          })
          .eq('id', editingLiability.id);

        if (error) throw error;
        setEditingLiability(null);
      } else {
        // Add new liability
        const { error } = await supabase
          .from('liabilities')
          .insert({
            user_id: session.user.id,
            name: liabilityForm.name,
            value: parseFloat(liabilityForm.value),
            monthly_payment: liabilityForm.monthly_payment ? parseFloat(liabilityForm.monthly_payment) : null,
            interest_rate: liabilityForm.interest_rate ? parseFloat(liabilityForm.interest_rate) : null,
            rate_type: liabilityForm.rate_type,
            category: liabilityForm.category,
            group: liabilityForm.group || null,
            date: liabilityForm.date
          });

        if (error) throw error;
      }

      setLiabilityForm({
        name: '',
        value: '',
        monthly_payment: '',
        interest_rate: '',
        rate_type: 'fixed',
        category: 'credit_card',
        group: '',
        date: new Date().toISOString().split('T')[0]
      });
      setShowLiabilityForm(false);
      onLiabilitiesChange();
    } catch (error) {
      console.error('Error with liability:', error);
      alert('Error with liability: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteAsset = async (id) => {
    if (!window.confirm('Are you sure you want to delete this asset?')) return;

    try {
      const { error } = await supabase
        .from('assets')
        .delete()
        .eq('id', id);

      if (error) throw error;
      onAssetsChange();
    } catch (error) {
      console.error('Error deleting asset:', error);
      alert('Error deleting asset: ' + error.message);
    }
  };

  const deleteLiability = async (id) => {
    if (!window.confirm('Are you sure you want to delete this liability?')) return;

    try {
      const { error } = await supabase
        .from('liabilities')
        .delete()
        .eq('id', id);

      if (error) throw error;
      onLiabilitiesChange();
    } catch (error) {
      console.error('Error deleting liability:', error);
      alert('Error deleting liability: ' + error.message);
    }
  };

  const editAsset = (asset) => {
    setEditingAsset(asset);
    setAssetForm({
      name: asset.name,
      value: asset.value.toString(),
      category: asset.category || 'cash',
      group: asset.group || '',
      date: asset.date
    });
    setShowAssetForm(true);
  };

  const editLiability = (liability) => {
    setEditingLiability(liability);
    setLiabilityForm({
      name: liability.name,
      value: liability.value.toString(),
      monthly_payment: liability.monthly_payment != null ? liability.monthly_payment.toString() : '',
      interest_rate: liability.interest_rate != null ? liability.interest_rate.toString() : '',
      rate_type: liability.rate_type || 'fixed',
      category: liability.category || 'credit_card',
      group: liability.group || '',
      date: liability.date
    });
    setShowLiabilityForm(true);
  };

  const cancelEdit = () => {
    setEditingAsset(null);
    setEditingLiability(null);
    setAssetForm({
      name: '',
      value: '',
      category: 'cash',
      group: '',
      date: new Date().toISOString().split('T')[0]
    });
    setLiabilityForm({
      name: '',
      value: '',
      monthly_payment: '',
      interest_rate: '',
      rate_type: 'fixed',
      category: 'credit_card',
      group: '',
      date: new Date().toISOString().split('T')[0]
    });
    setShowAssetForm(false);
    setShowLiabilityForm(false);
  };

  // Group assets and liabilities
  const groupedAssets = assets.reduce((groups, asset) => {
    const group = asset.group || 'Ungrouped';
    if (!groups[group]) groups[group] = [];
    groups[group].push(asset);
    return groups;
  }, {});

  const groupedLiabilities = liabilities.reduce((groups, liability) => {
    const group = liability.group || 'Ungrouped';
    if (!groups[group]) groups[group] = [];
    groups[group].push(liability);
    return groups;
  }, {});

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Assets Section */}
      <div className="card">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">Assets</h3>
            <button
              onClick={() => {
                setEditingAsset(null);
                setShowAssetForm(!showAssetForm);
              }}
              className="btn-success btn-sm"
            >
              <Plus className="h-4 w-4 mr-1.5" /> Add Asset
            </button>
          </div>
        </div>

        {showAssetForm && (
          <div className="p-6 border-b border-gray-200 bg-green-50">
            <form onSubmit={addAsset} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Asset Name
                  </label>
                  <input
                    type="text"
                    value={assetForm.name}
                    onChange={(e) => setAssetForm({...assetForm, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="e.g., Checking Account"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Value ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={assetForm.value}
                    onChange={(e) => setAssetForm({...assetForm, value: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="0.00"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    value={assetForm.category}
                    onChange={(e) => setAssetForm({...assetForm, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    {assetCategories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Group (Optional)
                  </label>
                  <input
                    type="text"
                    value={assetForm.group}
                    onChange={(e) => setAssetForm({...assetForm, group: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="e.g., Emergency Fund"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    value={assetForm.date}
                    onChange={(e) => setAssetForm({...assetForm, date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg text-sm font-medium"
                >
                  {loading ? (editingAsset ? 'Updating...' : 'Adding...') : (editingAsset ? 'Update Asset' : 'Add Asset')}
                </button>
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="p-6">
          {assets.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No assets added yet</p>
          ) : (
            <div className="space-y-4">
              {Object.entries(groupedAssets).map(([groupName, groupAssets]) => (
                <div key={groupName} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                      <h4 className="font-medium text-gray-900 text-sm">
                        {groupName} ({groupAssets.length})
                      </h4>
                      <p className="text-sm font-semibold text-green-600">
                        {formatCurrency(groupAssets.reduce((sum, asset) => sum + asset.value, 0))}
                      </p>
                    </div>
                  </div>
                  <div className="divide-y divide-gray-200">
                    {groupAssets.map(asset => (
                      <div key={asset.id} className="flex items-center justify-between p-4 hover:bg-gray-50 group">
                        <div className="flex items-center space-x-3">
                          <span className="text-2xl">{getCategoryIcon(asset.category, 'asset')}</span>
                          <div>
                            <p className="font-medium text-gray-900">{asset.name}</p>
                            <p className="text-sm text-gray-500">
                              {assetCategories.find(cat => cat.value === asset.category)?.label} • {formatDate(asset.date)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className="font-semibold text-green-600">{formatCurrency(asset.value)}</span>
                          <button
                            onClick={() => editAsset(asset)}
                            className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-blue-500 text-sm transition-opacity"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => deleteAsset(asset.id)}
                            className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 text-xl transition-opacity"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Liabilities Section */}
      <div className="card">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">Liabilities</h3>
            <button
              onClick={() => {
                setEditingLiability(null);
                setShowLiabilityForm(!showLiabilityForm);
              }}
              className="btn-danger btn-sm"
            >
              <Plus className="h-4 w-4 mr-1.5" /> Add Liability
            </button>
          </div>
        </div>

        {showLiabilityForm && (
          <div className="p-6 border-b border-gray-200 bg-red-50">
            <form onSubmit={addLiability} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Liability Name
                  </label>
                  <input
                    type="text"
                    value={liabilityForm.name}
                    onChange={(e) => setLiabilityForm({...liabilityForm, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="e.g., Credit Card"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount Owed ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={liabilityForm.value}
                    onChange={(e) => setLiabilityForm({...liabilityForm, value: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="0.00"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Monthly Payment ($) <span className="text-gray-400 font-normal">— optional</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={liabilityForm.monthly_payment}
                    onChange={(e) => setLiabilityForm({...liabilityForm, monthly_payment: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="e.g., 250.00"
                  />
                  <p className="text-xs text-gray-500 mt-1">Used in the Budget &amp; Debt tabs</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Interest Rate (%) <span className="text-gray-400 font-normal">— optional</span>
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      step="0.01"
                      value={liabilityForm.interest_rate}
                      onChange={(e) => setLiabilityForm({...liabilityForm, interest_rate: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="e.g., 19.99"
                    />
                    <select
                      value={liabilityForm.rate_type}
                      onChange={(e) => setLiabilityForm({...liabilityForm, rate_type: e.target.value})}
                      className="px-2 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                      title="Rate type"
                    >
                      <option value="fixed">Fixed</option>
                      <option value="variable">Variable</option>
                    </select>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Powers the Debt Payoff strategies</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    value={liabilityForm.category}
                    onChange={(e) => setLiabilityForm({...liabilityForm, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  >
                    {liabilityCategories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Group (Optional)
                  </label>
                  <input
                    type="text"
                    value={liabilityForm.group}
                    onChange={(e) => setLiabilityForm({...liabilityForm, group: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="e.g., High Interest Debt"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    value={liabilityForm.date}
                    onChange={(e) => setLiabilityForm({...liabilityForm, date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg text-sm font-medium"
                >
                  {loading ? (editingLiability ? 'Updating...' : 'Adding...') : (editingLiability ? 'Update Liability' : 'Add Liability')}
                </button>
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="p-6">
          {liabilities.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No liabilities added yet</p>
          ) : (
            <div className="space-y-4">
              {Object.entries(groupedLiabilities).map(([groupName, groupLiabilities]) => (
                <div key={groupName} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                      <h4 className="font-medium text-gray-900 text-sm">
                        {groupName} ({groupLiabilities.length})
                      </h4>
                      <p className="text-sm font-semibold text-red-600">
                        {formatCurrency(groupLiabilities.reduce((sum, liability) => sum + liability.value, 0))}
                      </p>
                    </div>
                  </div>
                  <div className="divide-y divide-gray-200">
                    {groupLiabilities.map(liability => (
                      <div key={liability.id} className="flex items-center justify-between p-4 hover:bg-gray-50 group">
                        <div className="flex items-center space-x-3">
                          <span className="text-2xl">{getCategoryIcon(liability.category, 'liability')}</span>
                          <div>
                            <p className="font-medium text-gray-900">{liability.name}</p>
                            <p className="text-sm text-gray-500">
                              {liabilityCategories.find(cat => cat.value === liability.category)?.label} • {formatDate(liability.date)}
                            </p>
                            {((liability.monthly_payment != null && liability.monthly_payment !== '') ||
                              (liability.interest_rate != null && liability.interest_rate !== '')) && (
                              <p className="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-x-2">
                                {liability.monthly_payment != null && liability.monthly_payment !== '' && (
                                  <span>💵 {formatCurrency(liability.monthly_payment)}/mo</span>
                                )}
                                {liability.interest_rate != null && liability.interest_rate !== '' && (
                                  <span>📊 {liability.interest_rate}% {(liability.rate_type || 'fixed') === 'variable' ? 'var' : 'fixed'}</span>
                                )}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className="font-semibold text-red-600">{formatCurrency(liability.value)}</span>
                          <button
                            onClick={() => editLiability(liability)}
                            className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-blue-500 text-sm transition-opacity"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => deleteLiability(liability.id)}
                            className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 text-xl transition-opacity"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssetLiabilityManager;